import { ColumnAggregation } from "./shared-enums";

/**
 * boolean shape of which aggregations a metric supports
 *
 * callers convert their own CanBeAveraged enum into the two booleans, so the helper stays out of the
 * wire-vs-ts enum-value mismatch (backend gets utils-isomorphic "arithmetic", frontend gets wire "Arithmetic")
 */
export type MetricAggregationFlags = {
  canBeTotaled: boolean;
  canBeArithmeticAveraged: boolean;
  canBeGeometricAveraged: boolean;
  canBeMaximized: boolean;
  canBeMinimized: boolean;
  canBeCounted: boolean;
};

/**
 * returns the aggregations a metric supports, in a stable display order
 *
 * order is sum, arithmetic mean, geometric mean, min, max, count
 * a metric can support at most one averaging method since canBeAveraged is exclusive on the entity
 */
export const getAvailableColumnAggregations = (flags: MetricAggregationFlags): ColumnAggregation[] => {
  const result: ColumnAggregation[] = [];
  if (flags.canBeTotaled) result.push(ColumnAggregation.SUM);
  if (flags.canBeArithmeticAveraged) result.push(ColumnAggregation.ARITHMETIC_MEAN);
  if (flags.canBeGeometricAveraged) result.push(ColumnAggregation.GEOMETRIC_MEAN);
  if (flags.canBeMinimized) result.push(ColumnAggregation.MIN);
  if (flags.canBeMaximized) result.push(ColumnAggregation.MAX);
  if (flags.canBeCounted) result.push(ColumnAggregation.COUNT);
  return result;
};

/**
 * picks the default aggregation when creating a new column on an annual report
 *
 * prefers arithmetic mean when available, falls back to sum
 * returns null when neither is available (annual rendering will leave the cell blank)
 */
export const getDefaultColumnAggregation = (flags: MetricAggregationFlags): ColumnAggregation | null => {
  const available = getAvailableColumnAggregations(flags);
  if (available.includes(ColumnAggregation.ARITHMETIC_MEAN)) return ColumnAggregation.ARITHMETIC_MEAN;
  if (available.includes(ColumnAggregation.SUM)) return ColumnAggregation.SUM;
  return null;
};

/**
 * true when the stored aggregation is still in the metric's valid set
 *
 * renderer uses this to decide whether to compute the cell or leave it blank when metric flags shift after a column is configured
 */
export const isColumnAggregationValidForMetric = (
  aggregation: ColumnAggregation | null,
  flags: MetricAggregationFlags,
): boolean => {
  if (aggregation === null) return false;
  return getAvailableColumnAggregations(flags).includes(aggregation);
};
